import {
  AccountBalanceOutlined,
  AddRounded,
  ContactsOutlined,
  DescriptionOutlined,
  GavelOutlined,
  GroupsOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  InsertDriveFileOutlined,
  OpenInNewRounded,
  PersonOutline,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { Box, Container, Tooltip } from "@mui/material";
import { isNil, size } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import LoanFilePreviewModal from "../../../../components/modals/files/LoanFilePreviewModal";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { AppraisalStatusChip } from "../../../../components/appraisalOrders/appraisalOrderUi";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import {
  useDrilldownBaseUrl,
  useIsAcquisitions,
} from "../../../../pipelines/PipelineContext";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import { loanFetchedDataLoanTermsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import {
  AssignedTeamRows,
  FONT_MONO,
  Hero,
} from "../LoanDrilldownSummaryTab/summaryShared";
import {
  BentoColumn,
  BentoGrid,
} from "../LoanDrilldownSummaryTab/summaryBento";
import { getEscrowAmountCollected } from "../../../../utils/loan/escrowCollection";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import UpbInfoBox, { hasBothUpbs } from "../../shared/upb/UpbInfoBox";
import UpbLeadCard from "../../shared/upb/UpbLeadCard";
import UpbOverTimeCard from "../../shared/upb/UpbOverTimeCard";
import { hasUpbChart } from "../../shared/upb/upbModel";

/* ═══════════════════════════════════════════════════════════
   CLOSER VIEW — a closing-desk-tailored summary layout.

   Shares the design tokens / Panel chrome of Summary Redesign 3
   but swaps in closer-relevant figures: wire amounts, closing
   files checklist, closing-document tracking, and a numbers/terms
   split on the loan-information card.

   Fields that the LOS data model does not yet back are rendered
   as read-only "—" rows (clearly labeled) so the layout matches
   the closing workflow; see the PR/commit notes for the list of
   backend fields these placeholders are waiting on.
   ═══════════════════════════════════════════════════════════ */
const C = {
  bg: "#f4f6f8",
  card: "#ffffff",
  ink: "#10151c",
  ink2: "#4a5560",
  ink3: "#8a96a3",
  line: "#e8ecf0",
  line2: "#eef1f4",
  accent: "#1f4e8c",
  blue: "#1f4e8c",
  blueSoft: "#eaf1fa",
  blueLine: "#dbe7f6",
  amber: "#b4690e",
  good: "#2d6a4a",
  goodSoft: "#e3efe7",
};
const SHADOW =
  "0 1px 2px rgba(16,21,28,.04), 0 6px 24px -12px rgba(16,21,28,.12)";

const sumDue = (items) =>
  (items || []).reduce((acc, x) => acc + (Number(x?.totalDueAmount) || 0), 0);

/* date helper — short month/day/year, e.g. "Mar 5, 2026" */
const shortDateYear = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(v, "", "MMM d, yyyy");
};

const dollar = (v) => (isNil(v) ? "—" : formatDollar(v, "—"));
const pctOrDash = (v) => (isNil(v) ? "—" : formatPercent(v));

// loanTerms takes precedence over the loan doc for fields that live on both.
const fld = (loanTerms, loan, key) => loanTerms?.[key] ?? loan?.[key];

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
const LoanDrilldownCloserViewTab = () => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const { getLoanTerms } = useLoanTermsHook();
  const navigate = useNavigate();
  // Pipeline-aware — see useDrilldownBaseUrl; /loan/:id would 404 an acquisition.
  const baseUrl = useDrilldownBaseUrl(loan?._id);
  const isAcquisition = useIsAcquisitions();

  // Closer figures (wire, escrow, fee splits) live on the loan-terms object,
  // fetched lazily the same way the Fees tab does. Don't refetch if a sibling
  // tab already populated the slice.
  useEffect(() => {
    if (isNil(loanTerms) && loan?._id) {
      getLoanTerms({
        dealId: loan._id,
        onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        onFailFn: () => dispatch(loanFetchedDataLoanTermsSet({})),
      });
    }
  }, [loanTerms, loan?._id]);

  return (
    <LoanDrilldownTabPanel>
      <Box
        sx={{
          minHeight: "100%",
          background: `radial-gradient(1200px 400px at 80% -10%, rgba(20,84,58,.05), transparent 60%), ${C.bg}`,
          p: { xs: "20px 16px", md: "28px" },
        }}
      >
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          <Hero loan={loan} />
          <AppraisalOrderBanner
            loan={loan}
            navigate={navigate}
            baseUrl={baseUrl}
          />
          <CloserMetrics
            loan={loan}
            loanTerms={loanTerms}
            navigate={navigate}
            baseUrl={baseUrl}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
              gap: "18px",
              alignItems: "start",
            }}
          >
            <LoanTermsAndNumbersPanel
              loan={loan}
              loanTerms={loanTerms}
              navigate={navigate}
              baseUrl={baseUrl}
            />
            <AssignedTeamPanel loan={loan} />
          </Box>

          {/* Purchased loan: the balance being bought + its path to maturity
              (same section as the Default view). Only when both UPBs are on
              the loan AND there is a chart to draw. */}
          {isAcquisition && hasUpbChart(loan) && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" },
                gap: "18px",
                alignItems: "start",
              }}
            >
              <UpbLeadCard
                loan={loan}
                sx={{ borderRadius: "16px", boxShadow: SHADOW }}
              />
              <UpbOverTimeCard
                loan={loan}
                sx={{
                  borderRadius: "16px",
                  border: `1px solid ${C.line}`,
                  boxShadow: SHADOW,
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
              gap: "18px",
              alignItems: "start",
            }}
          >
            <ApplicantsPanel
              loan={loan}
              navigate={navigate}
              baseUrl={baseUrl}
            />
            <PropertiesPanel
              loan={loan}
              navigate={navigate}
              baseUrl={baseUrl}
            />
            <ContactsPanel loan={loan} navigate={navigate} baseUrl={baseUrl} />
          </Box>

          <BentoGrid>
            <BentoColumn flex="1 1 320px" minWidth={300}>
              <WireInformationCard loan={loan} loanTerms={loanTerms} />
              <ClosingFilesCard
                loan={loan}
                navigate={navigate}
                baseUrl={baseUrl}
              />
            </BentoColumn>

            <BentoColumn flex="2 1 440px" minWidth={420}>
              <LoanInformationOverviewCard loan={loan} loanTerms={loanTerms} />
            </BentoColumn>

            <BentoColumn flex="1 1 320px" minWidth={300}>
              <ClosingDocumentCard loan={loan} loanTerms={loanTerms} />
            </BentoColumn>
          </BentoGrid>
        </Container>
      </Box>
    </LoanDrilldownTabPanel>
  );
};

/* ─── Appraisal order banner (carried over from the summary) ─── */
const AppraisalOrderBanner = ({ loan, navigate, baseUrl }) => {
  const orders = (loan?.appraisalOrders || []).filter(
    (o) => o?.status !== "cancelled",
  );
  if (orders.length === 0) return null;
  const latest = orders[0];
  const due = shortDateYear(latest?.requestedByDate);

  return (
    <Box
      onClick={() => navigate(`${baseUrl}/orders`)}
      role="button"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "16px",
        py: "11px",
        background: "#fffaf2",
        border: "1px solid #f0e0c8",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "background .15s, border-color .15s",
        "&:hover": { background: "#fdf3e3", borderColor: "#e7cfa8" },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fbe8d2",
          color: C.amber,
          flexShrink: 0,
        }}
      >
        <GavelOutlined sx={{ fontSize: 17 }} />
      </Box>
      <Flx column g={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#7a4f12" }}>
          Internal appraisal review
          {orders.length > 1 ? ` (${orders.length})` : ""} ordered
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: "#9a7330" }}>
          Sent to the internal appraisers
          {due ? ` · needed by ${due}` : ""}
        </Txt>
      </Flx>
      <AppraisalStatusChip status={latest?.status} />
      <Txt
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: C.amber,
          whiteSpace: "nowrap",
        }}
      >
        View in Orders →
      </Txt>
    </Box>
  );
};

/* ─── Closer metric row ────────────────────────────────────
   Five headline widgets, re-targeted for the closing desk:
   Total Loan · Initial Loan · Net Wire to Title · Net Wire to
   Correspondent · Close Date (Settlement Date on an acquisition). */
const CloserMetrics = ({ loan, loanTerms, navigate, baseUrl }) => {
  const wire = loanTerms?.wireInstructions;

  const correspondentFees = useMemo(
    () => sumDue(loanTerms?.correspondentFees),
    [loanTerms?.correspondentFees],
  );

  // Net wire to title = the wire actually cut to the title/closing agent
  // (the closer-entered wire amount on the loan terms).
  const netWireToTitle = wire?.wireAmount;

  // Net wire to correspondent — the LOS has no stored field for this, so we
  // show a transparent proxy: initial loan amount less Eastview's correspondent
  // fees. Replace once a backend "net wire to correspondent" value exists.
  const netWireToCorrespondent =
    isNil(loan?.baseLoanAmount) && !correspondentFees
      ? null
      : (Number(loan?.baseLoanAmount) || 0) - correspondentFees;

  // Preclose: the loan's close (origination, else expected closing).
  // Acquisition: OUR purchase — the firm settlementDate, else the expected
  // one. The correspondent's origination date sits in the drilldown header.
  const isAcquisition = useIsAcquisitions();
  const dateCard = isAcquisition
    ? {
        label: "Settlement Date",
        value: shortDateYear(
          loan?.settlementDate || loan?.expectedSettlementDate,
        ),
        sub: loan?.settlementDate
          ? "Settled"
          : loan?.expectedSettlementDate
            ? "Expected"
            : null,
      }
    : {
        label: "Close Date",
        value: shortDateYear(
          loan?.originationDate || loan?.expectedClosingDate,
        ),
        sub: loan?.originationDate
          ? "Funded"
          : loan?.expectedClosingDate
            ? "Expected"
            : null,
      };

  const cards = [
    {
      label: "Total Loan Amount",
      value: dollar(loan?.totalLoanAmount),
      sub: isNil(loan?.baseLoanToAsIsValue)
        ? "Base"
        : `Base · LTV ${formatPercent(loan?.baseLoanToAsIsValue)}`,
      lead: true,
    },
    {
      label: "Initial Loan Amount",
      value: dollar(loan?.baseLoanAmount),
      sub: isNil(loan?.totalHoldback)
        ? null
        : `Holdback ${formatDollar(loan?.totalHoldback)}`,
    },
    {
      label: "Net Wire to Title",
      value: dollar(netWireToTitle),
      sub: wire?.wireStatus || null,
      to: baseUrl ? `${baseUrl}/wire-and-funding` : null,
      tooltip:
        "The Wire Amount entered on the loan's wire instructions. Click to update it on Wire & Funding.",
    },
    {
      label: "Net Wire to Correspondent",
      value: dollar(netWireToCorrespondent),
      sub: correspondentFees
        ? `Less ${formatDollar(correspondentFees)} corr. fees`
        : null,
      to: baseUrl ? `${baseUrl}/fees` : null,
      tooltip:
        "Initial Loan Amount minus correspondent fees. Adjust the correspondent fees on the Fees tab. Click to open Fees.",
    },
    { ...dateCard, value: dateCard.value || "—" },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(5, 1fr)",
        },
        gap: "14px",
      }}
    >
      {cards.map((c) => {
        const clickable = !!c.to && typeof navigate === "function";
        const card = (
          <Box
            key={c.label}
            onClick={clickable ? () => navigate(c.to) : undefined}
            sx={{
              background: c.lead
                ? "linear-gradient(155deg,#eef7f1,#ffffff 70%)"
                : C.card,
              border: `1px solid ${c.lead ? "#c5dccd" : C.line}`,
              borderRadius: "14px",
              boxShadow: SHADOW,
              p: "18px 18px 16px",
              cursor: clickable ? "pointer" : "default",
              transition: "border-color .15s, box-shadow .15s",
              ...(clickable && {
                "&:hover": {
                  borderColor: "#7fae93",
                  boxShadow: "0 4px 14px rgba(26,61,46,0.12)",
                },
              }),
            }}
          >
            <Flx ac jb g={0.5}>
              <Txt
                sx={{
                  fontSize: 10.5,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: C.ink3,
                  fontWeight: 600,
                }}
              >
                {c.label}
              </Txt>
              {c.tooltip && (
                <InfoOutlined
                  sx={{ fontSize: 14, color: C.ink3, flexShrink: 0 }}
                />
              )}
            </Flx>
            <Txt
              sx={{
                fontFamily: FONT_MONO,
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                mt: "8px",
                color: C.ink,
              }}
            >
              {c.value}
            </Txt>
            {c.sub && (
              <Txt
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: C.ink3,
                  mt: "4px",
                }}
              >
                {c.sub}
              </Txt>
            )}
          </Box>
        );

        return c.tooltip ? (
          <Tooltip key={c.label} title={c.tooltip} arrow placement="top">
            {card}
          </Tooltip>
        ) : (
          card
        );
      })}
    </Box>
  );
};

/* ─── Panel shell + header ───────────────────────────────── */
const Panel = ({ children, sx }) => (
  <Box
    sx={{
      background: C.card,
      border: `1px solid ${C.line}`,
      borderRadius: "16px",
      boxShadow: SHADOW,
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const PanelHead = ({ icon, title, count, endContent }) => (
  <Flx ac jb sx={{ p: "16px 20px", borderBottom: `1px solid ${C.line2}` }}>
    <Flx ac g={1}>
      <Flx ac sx={{ color: C.ink3, ".MuiSvgIcon-root": { fontSize: "16px" } }}>
        {icon}
      </Flx>
      <Txt
        sx={{
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: C.ink,
        }}
      >
        {title}
      </Txt>
      {count != null && <Count>{count}</Count>}
    </Flx>
    {endContent}
  </Flx>
);

const Count = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 11,
      background: C.bg,
      border: `1px solid ${C.line}`,
      borderRadius: "6px",
      px: "7px",
      py: "1px",
      color: C.ink2,
    }}
  >
    {children}
  </Box>
);

const PanelLink = ({ children, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      fontSize: 11,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: C.accent,
      fontWeight: 600,
      cursor: "pointer",
      "&:hover": { opacity: 0.7 },
    }}
  >
    {children}
  </Box>
);

/* ─── Info row (label + value, optional chip / Σ marker) ──── */
const InfoRow = ({ k, v, type }) => (
  <Flx ac jb g={1.5} sx={{ py: "10px", borderBottom: `1px solid ${C.line2}` }}>
    <Txt
      sx={{
        fontSize: 11,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: C.ink3,
        fontWeight: 500,
      }}
    >
      {k}
    </Txt>
    {type === "chip" ? (
      <Box
        component="span"
        sx={{
          background: C.blueSoft,
          border: `1px solid ${C.blueLine}`,
          color: C.blue,
          fontFamily: FONT_MONO,
          fontSize: 11.5,
          fontWeight: 500,
          px: "9px",
          py: "2px",
          borderRadius: "7px",
        }}
      >
        {v || "—"}
      </Box>
    ) : (
      <Flx
        ac
        g={0.6}
        sx={{ fontFamily: FONT_MONO, fontSize: 12.5, fontWeight: 500 }}
      >
        {type === "sigma" && (
          <Box component="span" sx={{ color: C.accent, fontSize: 11 }}>
            Σ
          </Box>
        )}
        <span>{v || "—"}</span>
      </Flx>
    )}
  </Flx>
);

const FieldsPanel = ({ icon, title, rows, endContent }) => (
  <Panel>
    <PanelHead icon={icon} title={title} endContent={endContent} />
    <Box sx={{ p: "8px 20px 16px" }}>
      {rows.map((r) => (
        <InfoRow key={r.k} k={r.k} v={r.v} type={r.type} />
      ))}
    </Box>
  </Panel>
);

/* ─── Loan information card (Terms | Numbers) ──────────────
   Two lanes: loan-term attributes on the left, dollar/rate
   figures on the right. */
const LoanTermsAndNumbersPanel = ({ loan, loanTerms, navigate, baseUrl }) => {
  const isAcquisition = useIsAcquisitions();
  const amortizationType = fld(loanTerms, loan, "amortizationType");
  const interestOnlyMonths = fld(loanTerms, loan, "interestOnlyMonths");
  const prepayStructure =
    fld(loanTerms, loan, "prepaymentPenaltyStructure") ||
    (isNil(loan?.prepaymentPenaltyMonths)
      ? null
      : `${loan.prepaymentPenaltyMonths} mo penalty`);

  const amortizationDisplay = useMemo(() => {
    const io =
      interestOnlyMonths && Number(interestOnlyMonths) > 0
        ? `Interest Only · ${interestOnlyMonths} mo`
        : null;
    return [amortizationType, io].filter(Boolean).join(" · ") || null;
  }, [amortizationType, interestOnlyMonths]);

  const termRows = [
    {
      k: "Term (months)",
      v: isNil(loan?.termMonths) ? "—" : `${loan.termMonths}`,
    },
    { k: "Product", v: loan?.loanProductType, type: "chip" },
    { k: "Loan Purpose", v: loan?.loanPurpose, type: "chip" },
    { k: "Rate Type", v: loan?.interestCalcType, type: "chip" },
    { k: "Amortization", v: amortizationDisplay },
    { k: "Prepayment", v: prepayStructure },
  ];

  const numberRows = [
    { k: "Total Loan Amount", v: dollar(loan?.totalLoanAmount) },
    { k: "Initial Loan Amount", v: dollar(loan?.baseLoanAmount) },
    { k: "Construction Escrow", v: dollar(loan?.totalHoldback) },
    { k: "Interest Reserve", v: dollar(loan?.totalInterestReserves) },
    { k: "Interest Rate", v: pctOrDash(loan?.interestRate) },
    { k: "Interest Calc", v: loan?.interestCalcType, type: "chip" },
  ];

  return (
    <Panel>
      <PanelHead
        icon={<InfoOutlined />}
        title="Loan Information"
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/loan-information`)}>
            Edit Details →
          </PanelLink>
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: "32px",
          p: "4px 20px 16px",
        }}
      >
        <Box>
          <SubHeading>Loan Terms</SubHeading>
          {termRows.map((r) => (
            <InfoRow key={r.k} k={r.k} v={r.v} type={r.type} />
          ))}
        </Box>
        <Box>
          <SubHeading>Numbers</SubHeading>
          {isAcquisition && hasBothUpbs(loan) && (
            <UpbInfoBox
              loan={loan}
              onOpen={() => navigate(`${baseUrl}/economics`)}
            />
          )}
          {numberRows.map((r) => (
            <InfoRow key={r.k} k={r.k} v={r.v} type={r.type} />
          ))}
        </Box>
      </Box>
    </Panel>
  );
};

const SubHeading = ({ children }) => (
  <Txt
    sx={{
      fontSize: 10,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: C.accent,
      fontWeight: 700,
      pt: "10px",
      pb: "2px",
    }}
  >
    {children}
  </Txt>
);

/* ─── Assigned team ───────────────────────────────────────── */
const ASSIGNEE_FIELDS = [
  "accountManager",
  "salesAnalyst",
  "structurer",
  "underwriter",
  "loanCloser",
];

const AssignedTeamPanel = ({ loan }) => {
  const assignedCount = ASSIGNEE_FIELDS.filter((f) => loan?.[f]).length;
  return (
    <Panel sx={{ overflow: "unset" }}>
      <PanelHead
        icon={<GroupsOutlined />}
        title="Assigned Team"
        count={assignedCount}
      />
      <Box sx={{ p: "6px 12px 10px" }}>
        <AssignedTeamRows loan={loan} />
      </Box>
    </Panel>
  );
};

/* ─── Entity panels ───────────────────────────────────────── */
const EntityRow = ({ icon, name, sub, badge, onClick }) => (
  <Flx
    ac
    g={1.5}
    onClick={onClick}
    sx={{
      p: "13px 18px",
      borderBottom: `1px solid ${C.line2}`,
      cursor: onClick ? "pointer" : "default",
      transition: "background 0.15s",
      "&:hover": { background: C.bg },
    }}
  >
    <Flx
      center
      sx={{
        width: 32,
        height: 32,
        borderRadius: "9px",
        background: C.bg,
        border: `1px solid ${C.line}`,
        color: C.ink3,
        flexShrink: 0,
        ".MuiSvgIcon-root": { fontSize: "15px" },
      }}
    >
      {icon}
    </Flx>
    <Box sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: C.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Txt>
      {sub && (
        <Txt
          sx={{
            fontSize: 11.5,
            color: C.ink3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </Txt>
      )}
    </Box>
    {badge && (
      <Box
        component="span"
        sx={{
          ml: "auto",
          fontFamily: FONT_MONO,
          fontSize: 9.5,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          background: C.bg,
          border: `1px solid ${C.line}`,
          color: C.ink2,
          px: "7px",
          py: "3px",
          borderRadius: "6px",
          flexShrink: 0,
        }}
      >
        {badge}
      </Box>
    )}
  </Flx>
);

const EmptyRow = ({ label }) => (
  <Flx
    ac
    sx={{ p: "13px 18px", minHeight: 58, borderBottom: `1px solid ${C.line2}` }}
  >
    <Txt sx={{ fontSize: 13, color: C.ink3, fontStyle: "italic" }}>{label}</Txt>
  </Flx>
);

const AddRow = ({ label, onClick }) => (
  <Flx
    center
    g={1}
    onClick={onClick}
    sx={{
      p: "13px 18px",
      fontSize: 12,
      color: C.accent,
      fontWeight: 500,
      cursor: "pointer",
      background: "#fbfdfc",
      transition: "background 0.15s",
      "&:hover": { background: "rgba(33,150,181,0.10)" },
      ".MuiSvgIcon-root": { fontSize: "14px" },
    }}
  >
    <AddRounded />
    {label}
  </Flx>
);

const ApplicantsPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const borrowers = loan?.borrowers || [];
  return (
    <Panel>
      <PanelHead
        icon={<PersonOutline />}
        title="Applicants"
        count={size(borrowers)}
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/applicants`)}>
            View All →
          </PanelLink>
        }
      />
      {borrowers.map((b) => {
        const role = b?.isGuarantor ? "Guarantor" : "Primary";
        // Closing view surfaces % ownership of the borrowing entity in place
        // of the FICO score the summary view shows.
        const ownership = isNil(b?.entityPercentOwnership)
          ? null
          : `${formatPercent(b.entityPercentOwnership)} ownership`;
        return (
          <EntityRow
            key={b._id}
            icon={<PersonOutline />}
            name={
              `${b?.firstName || ""} ${b?.lastName || ""}`.trim() ||
              "Unnamed borrower"
            }
            sub={[role, ownership].filter(Boolean).join(" · ")}
            badge={role}
            onClick={() =>
              dispatch(
                detailSidebarOpen(
                  DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER,
                  b,
                  b?._id,
                  {
                    title:
                      `${b?.firstName || ""} ${b?.lastName || ""}`.trim() ||
                      "Borrower",
                    mode: "view",
                    source: "LoanDrilldownCloserViewTab",
                    sourceField: "borrowers",
                  },
                ),
              )
            }
          />
        );
      })}
      {borrowers.length === 0 && <EmptyRow label="No applicants added" />}
      <AddRow
        label="Add borrower"
        onClick={() =>
          dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_BORROWER))
        }
      />
    </Panel>
  );
};

const PropertiesPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const properties = loan?.subjectProperties || [];
  return (
    <Panel>
      <PanelHead
        icon={<HomeWorkOutlined />}
        title="Properties"
        count={size(properties)}
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/properties`)}>
            View All →
          </PanelLink>
        }
      />
      {properties.map((p) => {
        const cityState = [p?.address?.city, p?.address?.state]
          .filter(Boolean)
          .join(", ");
        return (
          <EntityRow
            key={p._id}
            icon={<HomeWorkOutlined />}
            name={addressLabel(p?.address) || "Unnamed property"}
            sub={[cityState, p?.propertyType].filter(Boolean).join(" · ")}
            badge="Subject"
            onClick={() =>
              dispatch(
                detailSidebarOpen(
                  DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
                  p,
                  p?._id,
                  {
                    title: addressLabel(p?.address) || "Property",
                    mode: "view",
                    source: "LoanDrilldownCloserViewTab",
                    sourceField: "subjectProperties",
                  },
                ),
              )
            }
          />
        );
      })}
      {properties.length === 0 && <EmptyRow label="No properties added" />}
      <AddRow
        label="Add property"
        onClick={() =>
          dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_PROPERTY))
        }
      />
    </Panel>
  );
};

const ContactsPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const contacts = loan?.loanRelatedContacts || [];
  return (
    <Panel>
      <PanelHead
        icon={<ContactsOutlined />}
        title="Contacts"
        count={size(contacts)}
        endContent={
          <PanelLink
            onClick={() => navigate(`${baseUrl}/loan-related-contacts`)}
          >
            View All →
          </PanelLink>
        }
      />
      {contacts.map((c) => (
        <EntityRow
          key={c._id}
          icon={<PersonOutline />}
          name={
            `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
            "Unnamed contact"
          }
          sub={c?.company || c?.emailAddress || null}
          badge={c?.loanRole || "Contact"}
          onClick={() =>
            dispatch(
              detailSidebarOpen(
                DETAIL_SIDEBAR_CONTENT_TYPES.LOAN_RELATED_CONTACT,
                c,
                c?._id,
                {
                  title:
                    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
                    "Contact",
                  mode: "view",
                  source: "LoanDrilldownCloserViewTab",
                  sourceField: "loanRelatedContacts",
                },
              ),
            )
          }
        />
      ))}
      {contacts.length === 0 && <EmptyRow label="No contacts added" />}
      <AddRow
        label="Add contact"
        onClick={() =>
          dispatch(
            sidebarTypeToggle(
              loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
            ),
          )
        }
      />
    </Panel>
  );
};

/* ─── Wire information card (replaces interest reserve) ───── */
const WireInformationCard = ({ loan, loanTerms }) => {
  const wire = loanTerms?.wireInstructions;
  const rows = [
    { k: "Wire Amount", v: dollar(wire?.wireAmount) },
    { k: "Request Date", v: shortDateYear(wire?.wireRequestDate) || "—" },
    { k: "Fed Ref Number", v: wire?.wireFedRefNumber || "—" },
    { k: "Wire Status", v: wire?.wireStatus, type: "chip" },
    {
      k: "Release Date",
      v: shortDateYear(wire?.closingWireReleaseDate) || "—",
    },
  ];
  return (
    <FieldsPanel
      icon={<AccountBalanceOutlined />}
      title="Wire Information"
      rows={rows}
    />
  );
};

/* ─── Closing files checklist ──────────────────────────────
   A checklist of the closing-specific deliverables — but instead
   of a check, each row links to the matching loan document when
   one is on file, or shows a "Pending" state when not. Matching
   is keyword-based against each document's docType/docGroup. */
const CLOSING_FILE_ITEMS = [
  { label: "Funding Shield Certificate", any: ["funding shield"] },
  {
    label: "Title Knight Approval / CPL",
    any: ["title knight", "closing protection", "title - cpl", " cpl"],
  },
  { label: "Approved HUD", any: ["hud"] },
  {
    label: "Closing Documents (Unsigned)",
    any: ["unexecuted loan package", "unexecuted", "unsigned"],
  },
  {
    label: "Signed Closing Documents",
    any: [
      "executed loan package",
      "executed & indexed",
      "executed full package",
      "executed loan agreement",
    ],
  },
  { label: "Insurance Invoice", any: ["insurance invoice"] },
  { label: "Additional Items to be Paid", any: ["invoice"], optional: true },
  { label: "Broker Invoice", all: ["broker", "invoice"], optional: true },
];

const docMatchesItem = (doc, item) => {
  const haystack = `${doc?.docType || ""} ${doc?.docGroup || ""}`.toLowerCase();
  if (!haystack.trim()) return false;
  if (item.all) return item.all.every((kw) => haystack.includes(kw));
  if (item.any) return item.any.some((kw) => haystack.includes(kw));
  return false;
};

const ClosingFilesCard = ({ loan, navigate, baseUrl }) => {
  const docs = useMemo(() => loan?.loanDocuments || [], [loan?.loanDocuments]);
  const [previewFile, setPreviewFile] = useState(null);

  const rows = useMemo(
    () =>
      CLOSING_FILE_ITEMS.map((item) => ({
        item,
        doc: docs.find((d) => docMatchesItem(d, item)) || null,
      })),
    [docs],
  );

  return (
    <Panel>
      <PanelHead
        icon={<ReceiptLongOutlined />}
        title="Closing Files"
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/documents`)}>
            All Docs →
          </PanelLink>
        }
      />
      <Box sx={{ p: "4px 0 4px" }}>
        {rows.map(({ item, doc }) => (
          <ClosingFileRow
            key={item.label}
            label={item.label}
            optional={item.optional}
            doc={doc}
            onOpen={() => doc && setPreviewFile(doc)}
          />
        ))}
      </Box>
      <LoanFilePreviewModal
        file={previewFile}
        open={!!previewFile}
        containerId={previewFile?.containerName || previewFile?.dealId}
        relatedFiles={docs}
        onClose={() => setPreviewFile(null)}
        onSelectRelatedFile={setPreviewFile}
      />
    </Panel>
  );
};

const ClosingFileRow = ({ label, optional, doc, onOpen }) => {
  const provided = !!doc;
  return (
    <Flx
      ac
      jb
      g={1.5}
      onClick={provided ? onOpen : undefined}
      sx={{
        px: "20px",
        py: "11px",
        borderBottom: `1px solid ${C.line2}`,
        cursor: provided ? "pointer" : "default",
        transition: "background 0.15s",
        "&:hover": provided ? { background: C.bg } : undefined,
      }}
    >
      <Flx ac g={1} sx={{ minWidth: 0 }}>
        <Flx
          center
          sx={{
            width: 24,
            height: 24,
            borderRadius: "6px",
            flexShrink: 0,
            background: provided ? C.goodSoft : C.bg,
            border: `1px solid ${provided ? "#bcdcc7" : C.line}`,
            color: provided ? C.good : C.ink3,
            ".MuiSvgIcon-root": { fontSize: "14px" },
          }}
        >
          {provided ? <InsertDriveFileOutlined /> : <DescriptionOutlined />}
        </Flx>
        <Txt
          sx={{
            fontSize: 12.5,
            fontWeight: 500,
            color: provided ? C.ink : C.ink2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
          {optional && (
            <Box component="span" sx={{ color: C.ink3, fontWeight: 400 }}>
              {" "}
              (if applicable)
            </Box>
          )}
        </Txt>
      </Flx>
      {provided ? (
        <Tooltip title={doc?.docType || "Open document"} arrow placement="left">
          <Flx
            ac
            g={0.5}
            sx={{
              flexShrink: 0,
              color: C.accent,
              fontSize: 11,
              fontWeight: 600,
              ".MuiSvgIcon-root": { fontSize: "14px" },
            }}
          >
            Open
            <OpenInNewRounded />
          </Flx>
        </Tooltip>
      ) : (
        <Txt
          sx={{
            flexShrink: 0,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: C.ink3,
          }}
        >
          Pending
        </Txt>
      )}
    </Flx>
  );
};

/* ─── Loan information overview (correspondent / pricing) ── */
const LoanInformationOverviewCard = ({ loan, loanTerms }) => {
  const buyRate = fld(loanTerms, loan, "buyRate") ?? loan?.lockBuyRate;
  const dscrBuyPrice = fld(loanTerms, loan, "dscrBuyPrice");

  // Monthly P&I — prefer a stored payment amount, otherwise estimate from the
  // note rate on the total loan (interest-only basis).
  const monthlyPi = useMemo(() => {
    const stored = fld(loanTerms, loan, "monthlyPaymentAmount");
    if (!isNil(stored)) return Number(stored);
    const total = loan?.totalLoanAmount;
    const rate = loan?.interestRate;
    if (isNil(total) || isNil(rate)) return null;
    return (Number(total) * Number(rate)) / 12;
  }, [loan, loanTerms]);

  const rows = [
    { k: "Origination Pts (Lender)", v: pctOrDash(loan?.originationPoints) },
    {
      k: "Origination Pts (Broker)",
      v: pctOrDash(loan?.brokerOriginationPoints),
    },
    {
      k: "Buy Rate / Yield",
      v: isNil(buyRate) ? "—" : pctOrDash(buyRate),
    },
    {
      k: "DSCR Buy Price",
      v: isNil(dscrBuyPrice) ? "—" : `${dscrBuyPrice}`,
    },
    { k: "Exit Fee (pts)", v: pctOrDash(loan?.exitFeePoints) },
    { k: "Discount / Buydown Pts", v: pctOrDash(loan?.discountPoints) },
    { k: "Monthly P&I", v: dollar(monthlyPi), type: "sigma" },
    { k: "DSCR", v: isNil(loan?.dscr) ? "—" : `${loan.dscr}` },
    { k: "IO Spread", v: "—" },
    { k: "B Piece", v: "—" },
  ];

  return (
    <Panel>
      <PanelHead icon={<InfoOutlined />} title="Loan Information Overview" />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: "32px",
          p: "8px 20px 4px",
        }}
      >
        {rows.map((r) => (
          <InfoRow key={r.k} k={r.k} v={r.v} type={r.type} />
        ))}
      </Box>
      <Box sx={{ p: "8px 20px 16px" }}>
        <SubHeading>Escrows</SubHeading>
        <EscrowList loanTerms={loanTerms} />
      </Box>
    </Panel>
  );
};

const EscrowList = ({ loanTerms }) => {
  const items = loanTerms?.escrowItems || [];
  if (isNil(loanTerms)) {
    return (
      <Txt sx={{ fontSize: 12, color: C.ink3, py: "8px" }}>
        Loading escrows…
      </Txt>
    );
  }
  if (items.length === 0) {
    return (
      <Txt sx={{ fontSize: 12, color: C.ink3, fontStyle: "italic", py: "8px" }}>
        No escrow items
      </Txt>
    );
  }
  return (
    <>
      {items.map((e, i) => {
        const label =
          [e?.escrowCategory, e?.escrowSubCategory]
            .filter(Boolean)
            .join(" · ") ||
          e?.paidTo ||
          `Escrow ${i + 1}`;
        const amount = getEscrowAmountCollected(e) ?? e?.annualAmount;
        return <InfoRow key={e?._id || i} k={label} v={dollar(amount)} />;
      })}
    </>
  );
};

/* ─── Closing document card (replaces refinance) ───────────
   Closing-document tracking. Maturity / first-payment dates and
   the ACH-form status come from real data; the doc-source,
   lender-of-record, rate, fee and tracking-number rows are
   placeholders awaiting backend fields. */
const ClosingDocumentCard = ({ loan, loanTerms }) => {
  const maturityDate = fld(loanTerms, loan, "maturityDate");
  const firstPaymentDate = fld(loanTerms, loan, "firstPaymentDate");

  // ACH form status inferred from the loan's documents.
  const achOnFile = (loan?.loanDocuments || []).some((d) =>
    `${d?.docType || ""}`.toLowerCase().includes("ach form"),
  );

  const rows = [
    { k: "Loan Doc Source", v: "—", type: "chip" },
    { k: "Date of Drafting", v: "—" },
    { k: "Lender of Record", v: "—", type: "chip" },
    { k: "MIN", v: "—" },
    { k: "Default Rate", v: "—" },
    { k: "Late Fee", v: "—" },
    { k: "Grace Days", v: "—" },
    { k: "Maturity Date", v: shortDateYear(maturityDate) || "—" },
    { k: "First Payment Date", v: shortDateYear(firstPaymentDate) || "—" },
    { k: "ACH Form", v: achOnFile ? "On file" : "—", type: "chip" },
    { k: "Collateral Tracking #", v: "—" },
    { k: "AOM Tracking #", v: "—" },
    { k: "Allonge Tracking #", v: "—" },
  ];

  return (
    <FieldsPanel
      icon={<DescriptionOutlined />}
      title="Closing Document"
      rows={rows}
    />
  );
};

export default LoanDrilldownCloserViewTab;
